import { FC, useContext, useRef } from 'react';

import { AddImageIcon, Button, Paragraph, Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';

import { useUploadFile } from 'hooks';
import { LocalizationContext } from 'localization';
import { isSandbox } from 'sandbox';
import notification from 'services/notificationService';

import s from './ClientLogo.module.scss';
import { FILE_MAXIMUM_SIZE } from './constants';
import { IClientImageProps } from './interfaces';

export const ClientLogo: FC<IClientImageProps> = ({
  onUpload,
  imageLink,
  style,
  disabled,
}) => {
  const { t } = useContext(LocalizationContext);
  const imgInput = useRef<HTMLInputElement>(null);

  const { uploadImage, isUploading } = useUploadFile(onUpload, {
    local: isSandbox,
  });

  return (
    <div className={s.imageContainer}>
      <div
        className={cn(
          s.uploadImage,
          imageLink && s.selected,
          disabled && s.disabled
        )}
        onClick={() => {
          if (isUploading || imageLink || disabled) return;

          imgInput.current?.click();
        }}
        style={style}
      >
        <Preloader loading={isUploading} small>
          <div className={s.circle} />
          {imageLink && !disabled && (
            <Button
              size="s"
              onlyIcon
              variant="secondary"
              iconName="EditIcon"
              onClick={() => {
                if (isUploading || disabled) return;

                imgInput.current?.click();
              }}
              className={s.edit_button}
            />
          )}
          {!imageLink ? (
            <div className={s.default}>
              <AddImageIcon color="light" />
              <Paragraph size="s" color="white">
                {t('clientLogo.title')}
              </Paragraph>
            </div>
          ) : (
            <img
              src={imageLink}
              alt={t('setting.shell.bg.alt')}
              className={s.image}
            />
          )}
        </Preloader>
      </div>
      <input
        ref={imgInput}
        type="file"
        accept=".png, .jpg, .jpeg, .svg"
        className={s.input}
        value=""
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (!file) {
            return;
          }

          if (file.size > FILE_MAXIMUM_SIZE) {
            return notification.info({
              t,
              type: 'warning',
              title: t('errors.upload.size'),
              message: t('errors.upload.size.description', 2),
            });
          }

          uploadImage(file);
        }}
      />
    </div>
  );
};
